/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Box,
  Button,
  ButtonProps,
  chakra,
  Divider,
  forwardRef,
  Heading,
  Icon,
  List,
  Stack,
  Tooltip,
  useBreakpointValue,
} from '@eigtech/ui-shared-dave'
import { getLog } from '@eigtech/ui-shared-logging'
import { createLink } from '@eigtech/ui-shared-router'
import { AnimatePresence, motion } from 'framer-motion'
import { createContext, JSX, ReactNode, useContext, useEffect, useState } from 'react'
import { BsChevronBarLeft as CollapseIcon, BsChevronBarRight as ExpandIcon } from 'react-icons/bs'

const log = getLog()

type NavigationContext<NavId extends string = string> = {
  activeNav: NavId
  setActiveNav: (navId: NavId) => void
  isCollapsed?: boolean
  setIsCollapsed?: (isCollapsed: boolean) => void
  navIds: NavId[]
  hasMounted: boolean
  isMobileNav?: boolean
}

export function createNav<NavId extends string = string>({
  navIds,
  defaultNav,
}: {
  navIds: NavId[]
  defaultNav: NavId
}) {
  const defaultContext = {
    activeNav: defaultNav,
    navIds: [...new Set(navIds)],
    hasMounted: false,
  } satisfies Partial<NavigationContext>

  const NavigationContext = createContext<NavigationContext | undefined>(undefined)

  function useNavigationContext() {
    const context = useContext(NavigationContext)

    if (!context) {
      throw new Error('NavigationProvider required to use useNavigationContext')
    }

    return context as unknown as NavigationContext<NavId>
  }

  function useButtonProps(props: ButtonProps) {
    const { isCollapsed } = useNavigationContext()
    const btnProps = {
      ...navButtonStyleProps,
      ...props,
      children: isCollapsed ? '' : props.children,
    } satisfies ButtonProps

    return btnProps
  }

  function Navigation({
    children,
    isCollapsed: isCollapsedSetting,
    setIsCollapsed,
    isMobileNav,
    ...props
  }: { children: ReactNode } & Pick<
    NavigationContext,
    'isCollapsed' | 'setIsCollapsed' | 'isMobileNav'
  >) {
    const [hasMounted, setHasMounted] = useState(false)
    useEffect(() => {
      setHasMounted(true)
    }, [])

    const [activeNav, setActiveNav] = useState<string>(defaultNav)

    const forceCollapse = !!useBreakpointValue({ base: true, lg: false })
    const isCollapsed = !isMobileNav && !!(forceCollapse || isCollapsedSetting)

    const containerProps = {
      w: isMobileNav ? 'full' : isCollapsed ? 14 : 64,
      transition: 'width 250ms',
    }

    return (
      <NavigationContext.Provider
        value={{
          ...defaultContext,
          ...props,
          hasMounted,
          activeNav,
          setActiveNav,
          isCollapsed,
          setIsCollapsed,
        }}
      >
        <Box {...containerProps}>
          <Stack
            {...containerProps}
            background="brandPrimary.500"
            color="white"
            h="full"
            id="navigation__navContainer"
            justifyContent="space-between"
            left="0"
            minH="100%"
            position="fixed"
            px="2"
            py="2"
            top="0"
          >
            {children}
          </Stack>
        </Box>
      </NavigationContext.Provider>
    )
  }

  function NavigationTop({ children }: { children: ReactNode }) {
    return <Stack id="navigation__top">{children}</Stack>
  }

  function NavigationHeading({ children }: { children: ReactNode }) {
    const { isMobileNav, isCollapsed } = useNavigationContext()

    if (isCollapsed) {
      return null
    }

    return (
      <>
        <Box id="navigation__heading" pt={isMobileNav ? '1' : '0'}>
          {typeof children === 'string' ? (
            <Heading
              fontSize={isMobileNav ? 'lg' : '2xl'}
              letterSpacing="wider"
              textAlign="center"
              textTransform="uppercase"
              whiteSpace="nowrap"
            >
              {children}
            </Heading>
          ) : (
            children
          )}
        </Box>

        <Box px={isMobileNav ? '16' : '8'}>
          <Divider />
        </Box>
      </>
    )
  }

  function Navs({ navs }: { navs: Record<NavId, JSX.Element> }) {
    const { activeNav, hasMounted } = useNavigationContext()

    return (
      <chakra.nav id="navigation__navs" position="relative">
        <AnimatePresence>
          <motion.div
            key={activeNav}
            animate="enter"
            custom={activeNav}
            exit="exit"
            initial={!hasMounted ? false : 'exit'}
            style={{ position: 'absolute', top: 0, width: '100%' }}
            variants={navTransitions}
          >
            {navs[activeNav]}
          </motion.div>
        </AnimatePresence>
      </chakra.nav>
    )
  }

  function Nav({ children }: { children: ReactNode }) {
    return (
      <List id="navigation__nav" spacing="1">
        {children}
      </List>
    )
  }

  function NavigationBottom({ children }: { children: ReactNode }) {
    return (
      <Stack id="navigation__bottom" spacing="1">
        {children}
      </Stack>
    )
  }

  const BaseButton = forwardRef<ButtonProps, 'button' | 'a'>(function BaseButton(props, ref) {
    const { isCollapsed } = useNavigationContext()
    const btnProps = useButtonProps(props)

    return (
      <Tooltip isDisabled={!isCollapsed || !props.children} label={props.children}>
        <Button ref={ref} {...props} {...btnProps} />
      </Tooltip>
    )
  })

  const BaseNavLink = createLink(function BaseNavLink(props: ButtonProps) {
    return <BaseButton {...props} />
  })

  const NavLink = ((props: any) => (
    <BaseNavLink
      activeProps={{ style: { backgroundColor: 'var(--chakra-colors-whiteAlpha-100)' } }}
      {...props}
    />
  )) as typeof BaseNavLink

  function NavExternalLink({ href, ...props }: ButtonProps & { href: string }) {
    return <BaseButton as="a" href={href} {...props} />
  }

  const NavButton = BaseButton

  function NavNavButton({ id, onClick, ...props }: ButtonProps & { id: NavId }) {
    const { setActiveNav } = useNavigationContext()

    return (
      <BaseButton
        {...props}
        onClick={(e) => {
          onClick?.(e)
          setActiveNav(id)
        }}
      />
    )
  }

  function NavigationCollapseButton() {
    const { isCollapsed, setIsCollapsed } = useNavigationContext()

    useEffect(() => {
      if (!setIsCollapsed) {
        log.warn(
          'Attempting to use NavigationCollapseButton without passing setIsCollapsed function to Navigation!'
        )
      }
    }, [setIsCollapsed])

    if (!setIsCollapsed) return null

    return (
      <NavButton
        id="navigation__collapse"
        leftIcon={<Icon as={isCollapsed ? ExpandIcon : CollapseIcon} />}
        onClick={() => setIsCollapsed(!isCollapsed)}
      >
        {isCollapsed ? 'Expand' : 'Collapse'}
      </NavButton>
    )
  }

  return {
    Navigation,
    NavigationTop,
    NavigationHeading,
    Navs,
    Nav,
    NavigationBottom,
    NavLink,
    NavExternalLink,
    NavButton,
    NavNavButton,
    NavigationCollapseButton,
  }
}

const navTransitions = {
  enter: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      damping: 25,
      stiffness: 180,
    },
  },
  exit: (activeNav: string) => ({
    x: activeNav !== 'main' ? 500 : -500,
    opacity: 0,
    transition: {
      duration: 0.15,
      ease: [0.4, 0, 0.2, 1],
    },
  }),
}

const navButtonStyleProps = {
  alignItems: 'center',
  borderRadius: 'md',
  color: 'white',
  cursor: 'pointer',
  display: 'flex',
  fontWeight: 'semibold',
  justifyContent: 'flex-start',
  p: '2',
  textAlign: 'left',
  textDecoration: 'none',
  w: 'full',
  _hover: {
    textDecoration: 'none',
    bg: 'rgba(255, 255, 255, 0.1)',
  },
} satisfies ButtonProps
